import { CartUpdateEvent } from '@theme/events';
import { rewardState } from './lumea-cart-reward-rules.js';

const element = document.getElementById('lumea-rewards-config');
if (element) {
  const config = JSON.parse(element.textContent);
  const root = config.root;
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: 'USD' }).format(cents / 100);
  const text = (key, amount) => config.text[key].replace('[amount]', money(amount || 0));
  let pending = false;
  let running = null;
  let blocked = '';
  let lastStatus = '';
  let timer;
  let checkingOut = false;
  const fingerprint = cart => JSON.stringify({ currency: cart.currency, items: cart.items.filter(item => item.properties?._lumea_reward !== 'gift150').map(item => [item.key, item.quantity, item.final_line_price]), codes: cart.discount_codes });
  const sections = () => [...new Set([...document.querySelectorAll('cart-items-component')].map(item => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
  const getCart = async () => {
    const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(config.text.error);
    return response.json();
  };
  const post = async (path, body) => {
    const response = await fetch(root + path, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...body, sections: sections(), sections_url: location.pathname }) });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || config.text.error);
    return data;
  };
  const announce = (cart, response) => document.dispatchEvent(new CartUpdateEvent(cart, 'lumea-rewards', { source: 'lumea-rewards', itemCount: cart.item_count, sections: response?.sections }));
  const paint = (cart, state) => {
    document.querySelectorAll('lumea-cart-rewards').forEach(panel => {
      panel.hidden = !cart.item_count || !state.usd;
      const shippingRemaining = Math.max(0, config.shippingThreshold - state.subtotal);
      const giftRemaining = Math.max(0, config.giftThreshold - state.eligible);
      panel.querySelector('[data-shipping-message]').textContent = shippingRemaining ? text('shippingRemaining', shippingRemaining) : text('shippingUnlocked');
      panel.querySelector('[data-gift-message]').textContent = state.free ? text('giftAdded') : !config.available ? text('unavailable') : state.hasCodes ? text('conflict') : giftRemaining ? text('giftRemaining', giftRemaining) : lastStatus || text('adding');
      for (const [name, value, threshold] of [['shipping', state.subtotal, config.shippingThreshold], ['gift', state.eligible, config.giftThreshold]]) {
        const progress = Math.max(0, Math.min(100, value / threshold * 100));
        const bar = panel.querySelector('[data-' + name + '-bar]');
        bar.setAttribute('aria-valuenow', String(Math.round(progress)));
        bar.querySelector('span').style.width = progress + '%';
      }
      panel.querySelector('[data-reward-status]').textContent = lastStatus;
    });
  };
  async function reconcile() {
    let cart = await getCart();
    let state = rewardState(cart, config);
    lastStatus = '';
    const invalid = state.gifts.filter((gift, index) => !state.canGift || index > 0 || gift.quantity !== 1);
    if (invalid.length) {
      const updates = Object.fromEntries(invalid.map(gift => [gift.key, state.canGift && gift === state.gifts[0] ? 1 : 0]));
      const response = await post('cart/update.js', { updates });
      cart = await getCart();
      announce(cart, response);
      state = rewardState(cart, config);
    }
    const signature = fingerprint(cart);
    if (state.canGift && !state.gifts.length && !state.free && blocked !== signature) {
      try {
        const response = await post('cart/add.js', { items: [{ id: config.variantId, quantity: 1, properties: { _lumea_reward: 'gift150', [config.text.giftProperty]: config.text.giftPropertyValue } }] });
        cart = await getCart();
        state = rewardState(cart, config);
        if (!state.free || !state.canGift) {
          if (state.gifts.length) await post('cart/update.js', { updates: Object.fromEntries(state.gifts.map(item => [item.key, 0])) });
          cart = await getCart();
          state = rewardState(cart, config);
          blocked = fingerprint(cart);
          lastStatus = text('notFree');
          announce(cart);
        } else announce(cart, response);
      } catch {
        cart = await getCart();
        state = rewardState(cart, config);
        if (state.gifts.some(item => item.final_line_price > 0)) {
          await post('cart/update.js', { updates: Object.fromEntries(state.gifts.map(item => [item.key, 0])) });
          cart = await getCart();
          state = rewardState(cart, config);
          announce(cart);
        }
        blocked = fingerprint(cart);
        lastStatus = text('unavailable');
      }
    } else if (state.gifts.length && !state.free) {
      const response = await post('cart/update.js', { updates: Object.fromEntries(state.gifts.map(item => [item.key, 0])) });
      cart = await getCart();
      state = rewardState(cart, config);
      blocked = fingerprint(cart);
      lastStatus = text('notFree');
      announce(cart, response);
    } else if (blocked === signature) lastStatus = text('notFree');
    paint(cart, state);
    return { cart, state };
  }
  function sync() {
    pending = true;
    if (running) return running;
    running = (async () => {
      let result;
      try { do { pending = false; result = await reconcile(); } while (pending); return result; }
      catch (error) { lastStatus = text('error'); document.querySelectorAll('[data-reward-status]').forEach(el => { el.textContent = lastStatus; }); throw error; }
      finally { running = null; }
    })();
    return running;
  }
  function schedule(event) {
    if (event?.detail?.data?.source === 'lumea-rewards') return;
    clearTimeout(timer);
    timer = setTimeout(() => sync().catch(() => {}), 120);
  }
  document.addEventListener('cart:update', schedule);
  document.addEventListener('discount:update', schedule);
  document.addEventListener('dialog:open', schedule);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('focus', schedule);
  window.addEventListener('click', async event => {
    const remove = event.target.closest?.('.cart-items__remove');
    const row = remove?.closest('[data-key]');
    if (remove && row?.dataset.key) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (remove.disabled) return;
      remove.disabled = true;
      try {
        const response = await post('cart/change.js', { id: row.dataset.key, quantity: 0 });
        const cart = await getCart();
        announce(cart, response);
        await sync();
      } catch { lastStatus = text('error'); document.querySelectorAll('[data-reward-status]').forEach(el => { el.textContent = lastStatus; }); }
      finally { remove.disabled = false; }
      return;
    }
    const checkout = event.target.closest?.('button[name="checkout"], a[href^="/checkout"]');
    if (!checkout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    checkout.setAttribute('aria-busy', 'true');
    try { await sync(); location.assign(root + 'checkout'); }
    catch { checkingOut = false; checkout.removeAttribute('aria-busy'); }
  }, true);
  window.addEventListener('submit', async event => {
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    try { await sync(); location.assign(root + 'checkout'); }
    catch { checkingOut = false; }
  }, true);
  customElements.define('lumea-cart-rewards', class extends HTMLElement { connectedCallback() { schedule(); } });
  sync().catch(() => {});
}
